import useClientEmailQuery from 'hooks/webapi/client/useClientEmailQuery';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDigitalSignature } from 'hooks/webapi';
import {
  ERRORS_TEMPLATES,
  isErrorTemplate,
} from 'utils/errorMessageValidation';
import OTP from 'components/OTP/OTP';
import useClientPhoneQuery from 'hooks/webapi/client/useClientPhoneQuery';
import styles from './DigitalSignature.module.scss';
import { Button, Timeout, Typography } from '../../../../components';

type FormValues = {
  digitalCode: string;
};

export type Props = {
  handleCloseDigitalSignature: () => void;
  handleAgreementValue: () => void;
};

function DigitalSignature({
  handleAgreementValue,
  handleCloseDigitalSignature,
}: Props) {
  const { t } = useTranslation(['digital_signature', 'error']);
  const { data: clientPhone, refetch: fetchClientPhone } =
    useClientPhoneQuery();
  const { data: clientEmail } = useClientEmailQuery(true);
  const [timerVisible, setTimerVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const [errorMsg, setError] = useState<string | undefined>(undefined);
  const [isWrongCode, setIsWrongCode] = useState(false);

  const { postVereficationCodeToClient, postDigitalSignature } =
    useDigitalSignature();

  useEffect(() => {
    getNewCode();
    if (!clientPhone) fetchClientPhone();
  }, []);

  const getNewCode = useCallback(async () => {
    setTimerVisible(true);
    await postVereficationCodeToClient();
  }, [postVereficationCodeToClient]);

  const handleSubmit = useCallback(
    async (values: { digitalCode: string }) => {
      try {
        setPending(true);
        await postDigitalSignature(values.digitalCode);
        handleCloseDigitalSignature();
        handleAgreementValue();
      } catch (e) {
        if (isErrorTemplate(e, ERRORS_TEMPLATES.INCORRECT_CODE)) {
          setIsWrongCode(true);
          setError(t('error:validation.incorrect_code'));
        } else {
          // Generic error
          setError(t('error:validation.wrong_digital_code'));
        }
      } finally {
        setPending(false);
      }
    },
    [handleAgreementValue, handleCloseDigitalSignature, postDigitalSignature],
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      digitalCode: '',
    },
    onSubmit: handleSubmit,
  });

  const onInput = (e: ChangeEvent<HTMLInputElement, Element>) => {
    setError(undefined);
    setIsWrongCode(false);
    formik.setFieldValue('digitalCode', e.target.value);
  };

  const handleOnTimeout = useCallback(() => {
    setTimerVisible(false);
  }, []);

  return (
    <div className={styles['modal-content']}>
      <Typography
        size="l"
        kind="h1"
        className={styles.title}
        dataTest="digitalSignature"
      >
        {t('title')}
      </Typography>

      <Typography className={styles.message} size="s" kind="p">
        <span
          data-test="digitalSignature-email-text"
          dangerouslySetInnerHTML={{
            __html: t('digital_info_pre_phone', {
              clientPhone: clientPhone?.mobilePhone,
              clientEmail: clientEmail?.email,
            }),
          }}
        />
      </Typography>

      <form className={styles.form} onSubmit={formik.handleSubmit}>
        <div className={styles.code}>
          <div>
            <Typography
              className={styles['enter-code']}
              size="m"
              kind="p"
              dataTest="digitalSignature-code-text"
            >
              {t('enter_code')}
            </Typography>
            <div className="flex justify-center lg:justify-start">
              <OTP
                dataTest="phone-verification"
                name="digitalCode"
                value={formik.values.digitalCode}
                onInput={onInput}
              />
            </div>

            {errorMsg && (
              <span
                className={styles['error-code-msg']}
                data-test="digitalSignature-wrongCode-text"
              >
                {errorMsg}
              </span>
            )}
            <Typography className={styles.timer} size="s">
              {timerVisible ? (
                <>
                  <span
                    className={styles.counter}
                    data-test="digitalSignature-counter"
                  >
                    <Timeout seconds={59} onTimeout={handleOnTimeout} />
                  </span>
                  <span
                    className={styles.muted}
                    data-test="digitalSignature-newCode-text"
                  >
                    {t('get_new_code')}
                  </span>
                </>
              ) : (
                <button
                  type="button"
                  onClick={getNewCode}
                  className={styles['get-new-code']}
                  data-test="digitalSignature-newCode-btn"
                >
                  {t('get_new_code')}
                </button>
              )}
            </Typography>
          </div>
        </div>

        <p
          className={styles['contact-us']}
          dangerouslySetInnerHTML={{
            __html: t('contact_us'),
          }}
          data-test="digitalSignature-contact-text"
        />

        <div className={styles['button-wrapper']}>
          <Button
            dataTest="digitalSignature-submit"
            disabled={!formik.isValid || isWrongCode}
            className={styles.submit}
            pending={pending}
            type="submit"
          >
            {t('btn')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default DigitalSignature;
